import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { ActivationTestFlow } from 'core/constants/activationTest';
import { ActivationTestRequest } from 'core/interface/activationTestPayload';

export interface TestEducation {
  id: number;
  study: { name: string };
  school: { name: string };
  start: string;
  end: string | null;
  degree: string;
  grade?: string;
  graduate: boolean;
}

interface ActivationTestModeValue {
  enabled: boolean;
  flow: ActivationTestFlow;
  educations: TestEducation[];
  addEducation: (education: TestEducation) => void;
  requests: ActivationTestRequest[];
  recordRequest: (request: ActivationTestRequest) => void;
}

const defaultValue: ActivationTestModeValue = {
  enabled: false,
  flow: 'candidate',
  educations: [],
  addEducation: () => undefined,
  requests: [],
  recordRequest: () => undefined
};

const ActivationTestModeContext =
  createContext<ActivationTestModeValue>(defaultValue);

export const useActivationTestMode = (): ActivationTestModeValue =>
  useContext(ActivationTestModeContext);

export const ActivationTestModeProvider: React.FC<{
  flow: ActivationTestFlow;
  children: React.ReactNode;
}> = ({ flow, children }) => {
  const [educations, setEducations] = useState<TestEducation[]>([]);
  const [requests, setRequests] = useState<ActivationTestRequest[]>([]);

  const addEducation = useCallback((education: TestEducation) => {
    setEducations((prev) => [...prev, education]);
  }, []);

  const recordRequest = useCallback((request: ActivationTestRequest) => {
    setRequests((prev) => {
      const withoutSameId = prev.filter((item) => item.id !== request.id);
      return [...withoutSameId, request];
    });
  }, []);

  const value = useMemo(
    () => ({
      enabled: true,
      flow,
      educations,
      addEducation,
      requests,
      recordRequest
    }),
    [flow, educations, addEducation, requests, recordRequest]
  );

  return (
    <ActivationTestModeContext.Provider value={value}>
      {children}
    </ActivationTestModeContext.Provider>
  );
};
